import { useCallback, useEffect, useRef, useState } from 'react';
import SchoolCalendarService from '../../services/SchoolCalendarService';

const emptyState = { data: null, error: null, loading: false };

export default function useSchoolCalendar({ schoolYearStart, contextKey, enabled = true }) {
  const key = JSON.stringify([contextKey, schoolYearStart, enabled]);
  const [state, setState] = useState(emptyState);
  const refreshRef = useRef(() => {});
  const refresh = useCallback(() => refreshRef.current(), []);

  useEffect(() => {
    let active = true;
    let sequence = 0;
    let controller;
    const load = async () => {
      if (!enabled || !contextKey) return;
      sequence += 1;
      const request = sequence;
      if (controller) controller.abort();
      controller = new AbortController();
      setState(previous => ({ key, data: previous.key === key ? previous.data : null, error: null, loading: true }));
      try {
        const data = await SchoolCalendarService.getCalendar(schoolYearStart, { signal: controller.signal });
        if (active && request === sequence) setState({ key, data, loading: false, error: null });
      } catch (error) {
        if (active && request === sequence) setState({ key, data: null, loading: false, error });
      }
    };
    const refreshVisible = () => { if (!document.hidden) load(); };
    refreshRef.current = load;
    load();
    const timer = window.setInterval(refreshVisible, 30000);
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      active = false;
      if (controller) controller.abort();
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
      refreshRef.current = () => {};
    };
  }, [key, schoolYearStart, contextKey, enabled]);

  // Effects run after rendering: never render the previous school's snapshot meanwhile.
  const currentState = state.key === key ? state : { ...emptyState, loading: Boolean(enabled && contextKey) };
  return { ...currentState, refresh };
}
